import React, { useState, useMemo } from "react";
import {
    Card,
    Row,
    Col,
    Form,
    InputNumber,
    Select,
    Input,
    Button,
    Table,
    Tag,
    Typography,
    Space,
    Divider,
    Alert,
    Modal,
    Statistic,
    Radio,
    message,
    Spin,
    Empty,
    Steps,
    Result,
    TreeSelect,
    Switch,
    Badge,
} from "antd";
import {
    SettingItem,
    SettingRow,
    SettingContent,
    SettingLabel,
    SettingDescription,
    StyledSwitch
} from "../../../styles/shared-components";
import {
    GiftOutlined,
    ThunderboltOutlined,
    HeartOutlined,
    SmileOutlined,
    TrophyOutlined,
    CalendarOutlined,
    StarOutlined,
    SendOutlined,
    EyeOutlined,
    MailOutlined,
    MessageOutlined,
    UserOutlined,
    ArrowLeftOutlined,
    CheckCircleOutlined,
    RobotOutlined,
    SettingOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
    previewCouponCampaign,
    executeCouponCampaign,
    getProductCategories,
    getCompany,
    editCompany,
} from "../../../service/client";
import { colors, spacing, shadows } from "../../../styles/theme";
import { useBranchContext } from "../../../contexts/branch-context";
import { currencySymbol } from "../../../utils/currency";
import { PillTabs } from "../../../styles/shared-components";
import { MarketingSms } from "../marketing-sms/MarketingSms";
import { MarketingEmail } from "../marketing-email/MarketingEmail";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// ============================================
// STYLED COMPONENTS
// ============================================

const ScenarioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const ScenarioCard = styled(Card) <{ $selected?: boolean; $accent: string }>`
  cursor: pointer;
  transition: all 0.25s ease;
  border: 2px solid ${(p) => (p.$selected ? p.$accent : colors.borderLight)} !important;
  background: ${(p) => (p.$selected ? `${p.$accent}08` : colors.white)} !important;
  border-radius: 12px !important;

  &:hover {
    border-color: ${(p) => p.$accent} !important;
    box-shadow: 0 4px 16px ${(p) => p.$accent}20;
    transform: translateY(-2px);
  }

  .scenario-icon {
    font-size: 28px;
    margin-bottom: 8px;
  }

  .scenario-title {
    font-size: 15px;
    font-weight: 600;
    color: ${colors.textPrimary};
    margin-bottom: 4px;
  }

  .scenario-desc {
    font-size: 13px;
    color: ${colors.textSecondary};
    line-height: 1.5;
  }

  .ant-card-body {
    padding: 20px !important;
  }
`;

const ConfigSection = styled.div`
  background: ${colors.backgroundLight};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid ${colors.borderLight};
`;

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PreviewCard = styled(Card)`
  border-radius: 12px !important;
  box-shadow: ${shadows.small} !important;
  margin-bottom: 16px;
`;

const StepHeader = styled.div`
  margin-bottom: 24px;
`;

const AutomationCard = styled(Card)`
  border-radius: 12px !important;
  margin-bottom: 16px;
  border: 1px solid ${colors.borderLight} !important;
  
  .ant-card-head {
      border-bottom: 1px solid ${colors.borderLight};
      background: ${colors.backgroundLight};
  }
`;

// ============================================
// SCENARIOS
// ============================================

const SCENARIOS = [
    {
        key: "loyalty",
        titleKey: "scenarioLoyaltyTitle",
        descriptionKey: "scenarioLoyaltyDescription",
        icon: <HeartOutlined />,
        accent: "#e11d48",
    },
    {
        key: "reactivation",
        titleKey: "scenarioReactivationTitle",
        descriptionKey: "scenarioReactivationDescription",
        icon: <ThunderboltOutlined />,
        accent: "#f59e0b",
    },
    {
        key: "welcome",
        titleKey: "scenarioWelcomeTitle",
        descriptionKey: "scenarioWelcomeDescription",
        icon: <SmileOutlined />,
        accent: "#10b981",
    },
    {
        key: "spending_threshold",
        titleKey: "scenarioSpendingThresholdTitle",
        descriptionKey: "scenarioSpendingThresholdDescription",
        icon: <TrophyOutlined />,
        accent: "#8b5cf6",
    },
    {
        key: "seasonal",
        titleKey: "scenarioSeasonalTitle",
        descriptionKey: "scenarioSeasonalDescription",
        icon: <CalendarOutlined />,
        accent: "#3b82f6",
    },
    {
        key: "vip",
        titleKey: "scenarioVipTitle",
        descriptionKey: "scenarioVipDescription",
        icon: <StarOutlined />,
        accent: "#d97706",
    },
    {
        key: "anniversary",
        titleKey: "scenarioAnniversaryTitle",
        descriptionKey: "scenarioAnniversaryDescription",
        icon: <GiftOutlined />,
        accent: "#ec4899",
    },
];

// ============================================
// MAIN COMPONENT
// ============================================

export const MarketingCoupons: React.FC = () => {
    const { t } = useTranslation();
    const { currentBranch } = useBranchContext();
    // SMS działa tylko dla oddziałów w PLN (HostedSMS = numery PL), więc reklamy SMS
    // ukrywamy przy innej walucie — spójne z blokadą wysyłki po stronie backendu.
    const smsEnabled = (currentBranch?.currency || "PLN").toUpperCase() === "PLN";
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
    const [form] = Form.useForm();
    const [automationForm] = Form.useForm();
    const [previewData, setPreviewData] = useState<any>(null);
    const [campaignResult, setCampaignResult] = useState<any>(null);
    const [savedPayload, setSavedPayload] = useState<any>(null);
    const [activeTab, setActiveTab] = useState("blast");
    const [automationModalVisible, setAutomationModalVisible] = useState(false);

    const queryClient = useQueryClient();

    const couponScope = Form.useWatch("couponScope", form);

    // Fetch product categories for TreeSelect (same as Coupons.tsx)
    const { data: categories } = useQuery("product-categories-for-campaigns", () =>
        getProductCategories()
    );

    // Fetch company data for automation config
    const { data: company, isLoading: isCompanyLoading } = useQuery("company", getCompany);

    const treeData = useMemo(() => {
        if (!categories) return [];
        return categories.map((category: any) => ({
            title: category.name,
            value: `category-${category.id}`,
            key: `category-${category.id}`,
            selectable: false,
            children: (category.productBases || []).map((product: any) => ({
                title: product.name,
                value: product.id,
                key: `product-${product.id}`,
            })),
        }));
    }, [categories]);

    const previewMutation = useMutation(previewCouponCampaign, {
        onSuccess: (data) => {
            setPreviewData(data);
            setCurrentStep(2);
        },
        onError: (error: any) => {
            message.error(error?.response?.data?.error || t("marketingCoupons.previewError"));
        },
    });

    const executeMutation = useMutation(executeCouponCampaign, {
        onSuccess: (data) => {
            setCampaignResult(data);
            setCurrentStep(3);
            message.success(t("marketingCoupons.couponsGenerated", { count: data.totalGenerated }));
        },
        onError: (error: any) => {
            message.error(error?.response?.data?.error || t("marketingCoupons.executeError"));
        },
    });

    const updateCompanyMutation = useMutation(
        (data: any) => editCompany({ id: company.id, data }),
        {
            onSuccess: () => {
                message.success(t("marketingCoupons.automationConfigSaved"));
                queryClient.invalidateQueries("company");
                setAutomationModalVisible(false);
            },
            onError: (error: any) => {
                message.error(t("marketingCoupons.automationConfigSaveError"));
            }
        }
    );

    const handleSelectScenario = (key: string) => {
        setSelectedScenario(key);
        // Set default form values based on scenario
        const defaults: Record<string, any> = {
            loyalty: { minOrders: 5, couponValue: 10, couponType: "PERCENTAGE" },
            reactivation: { inactiveDays: 30, couponValue: 15, couponType: "PERCENTAGE" },
            welcome: { couponValue: 10, couponType: "PERCENTAGE" },
            spending_threshold: { minSpent: 200, couponValue: 20, couponType: "FIXED_AMOUNT" },
            seasonal: { couponValue: 10, couponType: "PERCENTAGE" },
            vip: { topPercent: 10, couponValue: 20, couponType: "PERCENTAGE" },
            anniversary: { couponValue: 15, couponType: "PERCENTAGE" },
        };
        form.setFieldsValue({
            ...defaults[key],
            couponScope: "CART",
            maxUsageCount: 1,
            validityDays: 30,
            minimumOrderValue: 0,
            channel: "SMS",
            smsTemplate: t("marketingCoupons.defaultSmsTemplate"),
            emailSubject: t("marketingCoupons.defaultEmailSubject"),
            emailTemplate: t("marketingCoupons.defaultEmailTemplate"),
        });
        setCurrentStep(1);
    };

    const handlePreview = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                scenario: selectedScenario,
                ...values,
                minimumOrderValue: (values.minimumOrderValue || 0) * 100,
                minSpent: values.minSpent ? values.minSpent * 100 : undefined,
                couponValue:
                    values.couponType === "FIXED_AMOUNT"
                        ? values.couponValue * 100
                        : values.couponValue,
                productIds:
                    values.couponScope === "SPECIFIC_PRODUCTS" ? values.productIds : undefined,
            };
            setSavedPayload(payload);
            previewMutation.mutate(payload);
        } catch {
            // validation error
        }
    };

    const handleExecute = () => {
        Modal.confirm({
            title: t("marketingCoupons.confirmSendTitle"),
            content: (
                <div>
                    <p>
                        {t("marketingCoupons.confirmGeneratePrefix")} <strong>{previewData?.totalClients}</strong> {t("marketingCoupons.confirmGenerateSuffix")}
                    </p>
                    {previewData?.smsCount > 0 && (
                        <p>
                            {t("marketingCoupons.smsToSendLabel")} <strong>{previewData.smsCount}</strong> {t("marketingCoupons.smsAvailableInline", { count: previewData.smsLeft })}
                        </p>
                    )}
                    <p style={{ color: colors.error }}>{t("marketingCoupons.operationCannotBeUndone")}</p>
                </div>
            ),
            okText: t("marketingCoupons.sendCampaign"),
            cancelText: t("marketingCoupons.cancel"),
            okType: "primary",
            onOk: async () => {
                if (!savedPayload) {
                    message.error(t("marketingCoupons.noSavedConfig"));
                    return;
                }
                executeMutation.mutate(savedPayload);
            },
        });
    };

    const handleReset = () => {
        setCurrentStep(0);
        setSelectedScenario(null);
        setPreviewData(null);
        setCampaignResult(null);
        setSavedPayload(null);
        form.resetFields();
    };

    const handleOpenAutomationConfig = () => {
        if (!company) return;
        const currentConfig = company?.marketingAutomationConfig?.welcome || {
            isActive: false,
            channel: "SMS",
            code: "WITAJ",
            couponType: "PERCENTAGE",
            couponValue: 10,
            smsTemplate: t("marketingCoupons.welcomeDefaultSmsTemplate"),
            emailSubject: t("marketingCoupons.welcomeDefaultEmailSubject"),
            emailTemplate: t("marketingCoupons.welcomeDefaultEmailTemplate"),
        };

        const friendlyConfig = {
            ...currentConfig,
            couponValue: currentConfig.couponType === "FIXED_AMOUNT" ? (currentConfig.couponValue / 100) : currentConfig.couponValue
        };

        automationForm.setFieldsValue(friendlyConfig);
        setAutomationModalVisible(true);
    }

    const handleSaveAutomation = async () => {
        try {
            const values = await automationForm.validateFields();

            // Prepare payload
            const payload = {
                marketingAutomationConfig: {
                    ...company?.marketingAutomationConfig,
                    welcome: {
                        ...values,
                        couponValue: values.couponType === "FIXED_AMOUNT" ? Math.round(values.couponValue * 100) : values.couponValue,
                    }
                }
            };

            updateCompanyMutation.mutate(payload);
        } catch (e) {
            console.error(e);
        }
    }

    const scenarioConfig = useMemo(
        () => SCENARIOS.find((s) => s.key === selectedScenario),
        [selectedScenario]
    );

    const formatPrice = (cents: number) => `${(cents / 100).toFixed(2)} ${currencySymbol(currentBranch?.currency)}`;

    // Helper to get active status safely
    const isWelcomeActive = company?.marketingAutomationConfig?.welcome?.isActive;

    // ========== STEP 0: Select scenario ==========
    const renderScenarioSelection = () => (
        <div>
            <StepHeader>
                <Title level={4} style={{ margin: 0 }}>
                    {t("marketingCoupons.selectScenarioTitle")}
                </Title>
                <Text type="secondary">
                    {t("marketingCoupons.selectScenarioSubtitle")}
                </Text>
            </StepHeader>
            <ScenarioGrid>
                {SCENARIOS.map((s) => (
                    <ScenarioCard
                        key={s.key}
                        $selected={selectedScenario === s.key}
                        $accent={s.accent}
                        onClick={() => handleSelectScenario(s.key)}
                        hoverable
                    >
                        <div className="scenario-icon">{s.icon}</div>
                        <div className="scenario-title">{t(`marketingCoupons.${s.titleKey}`)}</div>
                        <div className="scenario-desc">{t(`marketingCoupons.${s.descriptionKey}`)}</div>
                    </ScenarioCard>
                ))}
            </ScenarioGrid>
        </div>
    );

    // ========== STEP 1: Configure campaign ==========
    const renderConfiguration = () => (
        <div>
            <StepHeader>
                <Space>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => setCurrentStep(0)}
                        type="text"
                    />
                    <div>
                        <Title level={4} style={{ margin: 0 }}>
                            {scenarioConfig ? t(`marketingCoupons.${scenarioConfig.titleKey}`) : ""} {t("marketingCoupons.configurationSuffix")}
                        </Title>
                        <Text type="secondary">{scenarioConfig ? t(`marketingCoupons.${scenarioConfig.descriptionKey}`) : ""}</Text>
                    </div>
                </Space>
            </StepHeader>

            <Form form={form} layout="vertical" size="large">
                {/* Scenario-specific conditions */}
                <ConfigSection>
                    <SectionTitle>
                        <UserOutlined /> {t("marketingCoupons.segmentationConditions")}
                    </SectionTitle>
                    <Row gutter={16}>
                        {selectedScenario === "loyalty" && (
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="minOrders"
                                    label={t("marketingCoupons.minOrdersLabel")}
                                    rules={[{ required: true, message: t("marketingCoupons.minOrdersRequired") }]}
                                >
                                    <InputNumber min={2} max={100} style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                        )}
                        {selectedScenario === "reactivation" && (
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="inactiveDays"
                                    label={t("marketingCoupons.inactiveDaysLabel")}
                                    rules={[{ required: true, message: t("marketingCoupons.daysRequired") }]}
                                >
                                    <InputNumber min={7} max={365} style={{ width: "100%" }} addonAfter={t("marketingCoupons.daysUnit")} />
                                </Form.Item>
                            </Col>
                        )}
                        {selectedScenario === "spending_threshold" && (
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="minSpent"
                                    label={t("marketingCoupons.minSpentLabel")}
                                    rules={[{ required: true, message: t("marketingCoupons.amountRequired") }]}
                                >
                                    <InputNumber min={1} style={{ width: "100%" }} addonAfter={t("marketingCoupons.currencyUnit")} />
                                </Form.Item>
                            </Col>
                        )}
                        {selectedScenario === "vip" && (
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="topPercent"
                                    label={t("marketingCoupons.topPercentLabel")}
                                    rules={[{ required: true, message: t("marketingCoupons.percentRequired") }]}
                                >
                                    <InputNumber min={1} max={50} style={{ width: "100%" }} addonAfter="%" />
                                </Form.Item>
                            </Col>
                        )}
                        {(selectedScenario === "welcome" ||
                            selectedScenario === "seasonal" ||
                            selectedScenario === "anniversary") && (
                                <Col xs={24}>
                                    <Alert
                                        type="info"
                                        showIcon
                                        message={
                                            selectedScenario === "welcome"
                                                ? t("marketingCoupons.alertWelcome")
                                                : selectedScenario === "anniversary"
                                                    ? t("marketingCoupons.alertAnniversary")
                                                    : t("marketingCoupons.alertSeasonal")
                                        }
                                    />
                                </Col>
                            )}
                    </Row>
                </ConfigSection>

                {/* Coupon configuration */}
                <ConfigSection>
                    <SectionTitle>
                        <GiftOutlined /> {t("marketingCoupons.couponConfiguration")}
                    </SectionTitle>
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="couponType"
                                label={t("marketingCoupons.discountTypeLabel")}
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Select.Option value="PERCENTAGE">{t("marketingCoupons.percentageOption")}</Select.Option>
                                    <Select.Option value="FIXED_AMOUNT">{t("marketingCoupons.fixedAmountOption")}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="couponValue"
                                label={t("marketingCoupons.discountValueLabel")}
                                rules={[{ required: true, message: t("marketingCoupons.valueRequired") }]}
                            >
                                <InputNumber
                                    min={1}
                                    max={100}
                                    style={{ width: "100%" }}
                                    addonAfter={
                                        form.getFieldValue("couponType") === "FIXED_AMOUNT"
                                            ? t("marketingCoupons.currencyUnit")
                                            : "%"
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="validityDays"
                                label={t("marketingCoupons.validityLabel")}
                                rules={[{ required: true }]}
                            >
                                <InputNumber min={1} max={365} style={{ width: "100%" }} addonAfter={t("marketingCoupons.daysUnit")} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="maxUsageCount" label={t("marketingCoupons.maxUsageLabel")}>
                                <InputNumber min={1} max={100} style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="minimumOrderValue" label={t("marketingCoupons.minOrderValueLabel")}>
                                <InputNumber min={0} style={{ width: "100%" }} addonAfter={t("marketingCoupons.currencyUnit")} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="couponScope"
                                label={t("marketingCoupons.scopeLabel")}
                                rules={[{ required: true, message: t("marketingCoupons.scopeRequired") }]}
                            >
                                <Select>
                                    <Select.Option value="CART">{t("marketingCoupons.scopeCart")}</Select.Option>
                                    <Select.Option value="SPECIFIC_PRODUCTS">{t("marketingCoupons.scopeSpecificProducts")}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        {couponScope === "SPECIFIC_PRODUCTS" && (
                            <Col xs={24}>
                                <Form.Item
                                    name="productIds"
                                    label={t("marketingCoupons.selectProductsLabel")}
                                    rules={[{
                                        required: true,
                                        message: t("marketingCoupons.selectAtLeastOneProduct"),
                                    }]}
                                >
                                    <TreeSelect
                                        treeData={treeData}
                                        placeholder={t("marketingCoupons.selectProductsPlaceholder")}
                                        multiple
                                        showSearch
                                        treeCheckable
                                        showCheckedStrategy={TreeSelect.SHOW_CHILD}
                                        style={{ width: '100%' }}
                                        treeDefaultExpandAll
                                        filterTreeNode={(input, node) =>
                                            (node.title as string)
                                                ?.toLowerCase()
                                                ?.indexOf(input.toLowerCase()) >= 0
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        )}
                    </Row>
                </ConfigSection>

                {/* Delivery configuration */}
                <ConfigSection>
                    <SectionTitle>
                        <SendOutlined /> {t("marketingCoupons.deliveryChannel")}
                    </SectionTitle>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="channel" label={t("marketingCoupons.channelLabel")} rules={[{ required: true }]}>
                                <Radio.Group>
                                    <Radio.Button value="SMS">
                                        <MessageOutlined /> SMS
                                    </Radio.Button>
                                    <Radio.Button value="EMAIL">
                                        <MailOutlined /> {t("marketingCoupons.emailChannel")}
                                    </Radio.Button>
                                    <Radio.Button value="BOTH">
                                        <SendOutlined /> {t("marketingCoupons.bothChannel")}
                                    </Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => prev.channel !== curr.channel}
                    >
                        {({ getFieldValue }) => {
                            const channel = getFieldValue("channel");
                            return (
                                <Row gutter={16}>
                                    {(channel === "SMS" || channel === "BOTH") && (
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="smsTemplate"
                                                label={t("marketingCoupons.smsTemplateLabel")}
                                                extra={t("marketingCoupons.smsTemplateExtra")}
                                            >
                                                <TextArea rows={3} />
                                            </Form.Item>
                                        </Col>
                                    )}
                                    {(channel === "EMAIL" || channel === "BOTH") && (
                                        <Col xs={24} md={12}>
                                            <Form.Item name="emailSubject" label={t("marketingCoupons.emailSubjectLabel")}>
                                                <Input />
                                            </Form.Item>
                                            <Form.Item
                                                name="emailTemplate"
                                                label={t("marketingCoupons.emailTemplateLabel")}
                                                extra={t("marketingCoupons.emailTemplateExtra")}
                                            >
                                                <TextArea rows={3} />
                                            </Form.Item>
                                        </Col>
                                    )}
                                </Row>
                            );
                        }}
                    </Form.Item>
                </ConfigSection>

                <Space>
                    <Button onClick={() => setCurrentStep(0)}>{t("marketingCoupons.back")}</Button>
                    <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={handlePreview}
                        loading={previewMutation.isLoading}
                        size="large"
                    >
                        {t("marketingCoupons.previewCampaign")}
                    </Button>
                </Space>
            </Form>
        </div>
    );

    // ========== STEP 2: Preview ==========
    const renderPreview = () => (
        <div>
            <StepHeader>
                <Space>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => setCurrentStep(1)}
                        type="text"
                    />
                    <div>
                        <Title level={4} style={{ margin: 0 }}>
                            {scenarioConfig ? t(`marketingCoupons.${scenarioConfig.titleKey}`) : ""} {t("marketingCoupons.previewSuffix")}
                        </Title>
                        <Text type="secondary">
                            {t("marketingCoupons.previewSubtitle")}
                        </Text>
                    </div>
                </Space>
            </StepHeader>

            {previewData && (
                <>
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col xs={12} sm={6}>
                            <PreviewCard>
                                <Statistic
                                    title={t("marketingCoupons.statClients")}
                                    value={previewData.totalClients}
                                    prefix={<UserOutlined />}
                                />
                            </PreviewCard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <PreviewCard>
                                <Statistic
                                    title={t("marketingCoupons.statCouponsToGenerate")}
                                    value={previewData.totalClients}
                                    prefix={<GiftOutlined />}
                                />
                            </PreviewCard>
                        </Col>
                        {previewData.smsCount > 0 && (
                            <Col xs={12} sm={6}>
                                <PreviewCard>
                                    <Statistic
                                        title={t("marketingCoupons.statSmsToDeduct")}
                                        value={previewData.smsCount}
                                        prefix={<MessageOutlined />}
                                        valueStyle={
                                            previewData.smsCount > previewData.smsLeft
                                                ? { color: colors.error }
                                                : {}
                                        }
                                    />
                                </PreviewCard>
                            </Col>
                        )}
                        {previewData.smsCount > 0 && (
                            <Col xs={12} sm={6}>
                                <PreviewCard>
                                    <Statistic
                                        title={t("marketingCoupons.statSmsAvailable")}
                                        value={previewData.smsLeft}
                                        prefix={<MessageOutlined />}
                                    />
                                </PreviewCard>
                            </Col>
                        )}
                    </Row>

                    {previewData.smsCount > previewData.smsLeft && (
                        <Alert
                            type="error"
                            showIcon
                            message={t("marketingCoupons.notEnoughSms")}
                            description={t("marketingCoupons.notEnoughSmsDescription", { needed: previewData.smsCount, available: previewData.smsLeft })}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {previewData.totalClients === 0 ? (
                        <Empty
                            description={t("marketingCoupons.noEligibleClients")}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    ) : (
                        <>
                            <Table
                                dataSource={previewData.eligibleClients}
                                columns={[
                                    {
                                        title: t("marketingCoupons.colClient"),
                                        dataIndex: "name",
                                        key: "name",
                                        render: (name: string) => name || "—",
                                    },
                                    {
                                        title: t("marketingCoupons.colPhone"),
                                        dataIndex: "phoneNumber",
                                        key: "phoneNumber",
                                    },
                                    {
                                        title: t("marketingCoupons.colEmail"),
                                        dataIndex: "email",
                                        key: "email",
                                        ellipsis: true,
                                    },
                                    {
                                        title: t("marketingCoupons.colOrders"),
                                        dataIndex: "totalOrders",
                                        key: "totalOrders",
                                        sorter: (a: any, b: any) => a.totalOrders - b.totalOrders,
                                    },
                                    {
                                        title: t("marketingCoupons.colSpent"),
                                        dataIndex: "totalSpent",
                                        key: "totalSpent",
                                        render: (val: number) => formatPrice(val),
                                        sorter: (a: any, b: any) => a.totalSpent - b.totalSpent,
                                    },
                                    {
                                        title: t("marketingCoupons.colLastOrder"),
                                        dataIndex: "daysSinceLastOrder",
                                        key: "daysSinceLastOrder",
                                        render: (days: number | null) =>
                                            days !== null ? (
                                                <Tag color={days > 30 ? "orange" : "green"}>
                                                    {t("marketingCoupons.daysAgo", { days })}
                                                </Tag>
                                            ) : (
                                                "—"
                                            ),
                                    },
                                ]}
                                rowKey="id"
                                size="small"
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: 700 }}
                            />

                            <Divider />

                            <Space>
                                <Button onClick={() => setCurrentStep(1)}>
                                    {t("marketingCoupons.backToConfiguration")}
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<SendOutlined />}
                                    onClick={handleExecute}
                                    loading={executeMutation.isLoading}
                                    size="large"
                                    disabled={
                                        previewData.totalClients === 0 ||
                                        (previewData.smsCount > 0 &&
                                            previewData.smsCount > previewData.smsLeft)
                                    }
                                >
                                    {t("marketingCoupons.generateAndSend", { count: previewData.totalClients })}
                                </Button>
                            </Space>
                        </>
                    )}
                </>
            )}
        </div>
    );

    // ========== STEP 3: Result ==========
    const renderResult = () => (
        <Result
            status="success"
            title={t("marketingCoupons.campaignSentTitle")}
            subTitle={t("marketingCoupons.campaignSentSubtitle", { generated: campaignResult?.totalGenerated, sms: campaignResult?.smsCount, email: campaignResult?.emailCount })}
            extra={[
                <Button type="primary" key="console" onClick={handleReset}>
                    {t("marketingCoupons.newCampaign")}
                </Button>,
            ]}
        />
    );

    // ========== AUTOMATION TAB ==========

    const renderAutomationTab = () => (
        <div>
            <StepHeader>
                <Title level={4} style={{ margin: 0 }}>
                    <RobotOutlined /> {t("marketingCoupons.automationTitle")}
                </Title>
                <Text type="secondary">
                    {t("marketingCoupons.automationSubtitle")}
                </Text>
            </StepHeader>

            <AutomationCard>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{
                            background: isWelcomeActive ? '#e6fffa' : '#f5f5f5',
                            padding: 16,
                            borderRadius: '50%',
                            color: isWelcomeActive ? '#10b981' : '#ccc'
                        }}>
                            <SmileOutlined style={{ fontSize: 32 }} />
                        </div>
                        <div>
                            <Title level={5} style={{ margin: 0 }}>{t("marketingCoupons.welcomeCardTitle")}</Title>
                            <Paragraph type="secondary" style={{ margin: 0 }}>
                                {t("marketingCoupons.welcomeCardDescription")}
                            </Paragraph>
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <Tag color={isWelcomeActive ? "success" : "default"} style={{ marginBottom: 8, display: 'inline-block' }}>
                            {isWelcomeActive ? t("marketingCoupons.statusActive") : t("marketingCoupons.statusInactive")}
                        </Tag>
                        <div>
                            <Button icon={<SettingOutlined />} onClick={handleOpenAutomationConfig}>
                                {t("marketingCoupons.configure")}
                            </Button>
                        </div>
                    </div>
                </div>
            </AutomationCard>

            {/* Modal for configuration */}
            <Modal
                title={t("marketingCoupons.welcomeModalTitle")}
                open={automationModalVisible}
                onCancel={() => setAutomationModalVisible(false)}
                onOk={handleSaveAutomation}
                okText={t("marketingCoupons.saveChanges")}
                confirmLoading={updateCompanyMutation.isLoading}
                width={700}
            >
                {isCompanyLoading ? (
                    <Spin />
                ) : (
                    <Form form={automationForm} layout="vertical">
                        <SettingItem>
                            <SettingRow>
                                <Form.Item name="isActive" valuePropName="checked" noStyle>
                                    <StyledSwitch />
                                </Form.Item>
                                <SettingContent>
                                    <SettingLabel>{t("marketingCoupons.automationStatusLabel")}</SettingLabel>
                                    <SettingDescription>{t("marketingCoupons.automationStatusDescription")}</SettingDescription>
                                </SettingContent>
                            </SettingRow>
                        </SettingItem>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="code" label={t("marketingCoupons.couponPrefixLabel")} rules={[{ required: true }]}>
                                    <Input placeholder={t("marketingCoupons.couponPrefixPlaceholder")} style={{ textTransform: 'uppercase' }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="channel" label={t("marketingCoupons.deliveryChannelLabel")} rules={[{ required: true }]}>
                                    <Select>
                                        <Select.Option value="SMS">{t("marketingCoupons.onlySms")}</Select.Option>
                                        <Select.Option value="EMAIL">{t("marketingCoupons.onlyEmail")}</Select.Option>
                                        <Select.Option value="BOTH">{t("marketingCoupons.smsPlusEmail")}</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="couponType" label={t("marketingCoupons.discountTypeLabel2")}>
                                    <Select>
                                        <Select.Option value="PERCENTAGE">{t("marketingCoupons.percentageOption")}</Select.Option>
                                        <Select.Option value="FIXED_AMOUNT">{t("marketingCoupons.fixedAmountOption")}</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="couponValue" label={t("marketingCoupons.discountValueLabel2")}>
                                    <InputNumber style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="couponScope"
                                    label={t("marketingCoupons.scopeLabel")}
                                    rules={[{ required: true, message: t("marketingCoupons.scopeRequired") }]}
                                    initialValue="CART"
                                >
                                    <Select>
                                        <Select.Option value="CART">{t("marketingCoupons.scopeCart")}</Select.Option>
                                        <Select.Option value="SPECIFIC_PRODUCTS">{t("marketingCoupons.scopeSpecificProducts")}</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, curr) => prev.couponScope !== curr.couponScope}
                        >
                            {({ getFieldValue }) => {
                                const scope = getFieldValue("couponScope");
                                return scope === "SPECIFIC_PRODUCTS" ? (
                                    <Form.Item
                                        name="productIds"
                                        label={t("marketingCoupons.selectProductsLabel")}
                                        rules={[{ required: true, message: t("marketingCoupons.selectProductsRequired") }]}
                                    >
                                        <TreeSelect
                                            treeData={treeData}
                                            placeholder={t("marketingCoupons.selectProductsPlaceholder")}
                                            multiple
                                            showSearch
                                            treeCheckable
                                            showCheckedStrategy={TreeSelect.SHOW_CHILD}
                                            style={{ width: '100%' }}
                                            treeDefaultExpandAll
                                            filterTreeNode={(input: any, node: any) =>
                                                (node.title as string)?.toLowerCase()?.indexOf(input.toLowerCase()) >= 0
                                            }
                                        />
                                    </Form.Item>
                                ) : null;
                            }}
                        </Form.Item>

                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, curr) => prev.channel !== curr.channel}
                        >
                            {({ getFieldValue }) => {
                                const channel = getFieldValue("channel");
                                return (
                                    <>
                                        {(channel === "SMS" || channel === "BOTH") && (
                                            <Form.Item
                                                name="smsTemplate"
                                                label={t("marketingCoupons.smsContentLabel")}
                                                extra={t("marketingCoupons.smsVariablesExtra")}
                                            >
                                                <TextArea rows={3} showCount maxLength={160} />
                                            </Form.Item>
                                        )}
                                        {(channel === "EMAIL" || channel === "BOTH") && (
                                            <>
                                                <Form.Item name="emailSubject" label={t("marketingCoupons.emailTopicLabel")}>
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item
                                                    name="emailTemplate"
                                                    label={t("marketingCoupons.emailContentLabel")}
                                                    extra={t("marketingCoupons.emailVariablesExtra")}
                                                >
                                                    <TextArea rows={4} />
                                                </Form.Item>
                                            </>
                                        )}
                                    </>
                                );
                            }}
                        </Form.Item>
                    </Form>
                )}
            </Modal>
        </div>
    );

    // ========== DIRECT SEND TAB (SMS + Email) ==========
    const renderDirectTab = () => (
        <div>
            <StepHeader>
                <Title level={4} style={{ margin: 0 }}>
                    {t("marketingCoupons.directSendTitle")}
                </Title>
                <Text type="secondary">
                    {t("marketingCoupons.directSendSubtitle")}
                </Text>
            </StepHeader>

            {smsEnabled && (
                <AutomationCard title={t("marketingCoupons.smsSendCard")}>
                    <MarketingSms />
                </AutomationCard>
            )}

            <AutomationCard title={t("marketingCoupons.emailSendCard")}>
                <MarketingEmail />
            </AutomationCard>
        </div>
    );

    const tabItems = [
        {
            key: "blast",
            label: t("marketingCoupons.tabCampaigns"),
            children: (
                <>
                    {currentStep === 0 && renderScenarioSelection()}
                    {currentStep === 1 && renderConfiguration()}
                    {currentStep === 2 && renderPreview()}
                    {currentStep === 3 && renderResult()}
                </>
            ),
        },
        {
            key: "direct",
            label: t("marketingCoupons.tabDirectSend"),
            children: renderDirectTab(),
        },
        {
            key: "automation",
            label: (
                <span>
                    {t("marketingCoupons.tabAutomation")}
                    {isWelcomeActive && <Badge status="processing" style={{ marginLeft: 8 }} />}
                </span>
            ),
            children: renderAutomationTab(),
        },
    ];

    return (
        <PillTabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    );
};
